@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginemodule

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.ScreenManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun ShopModuleDetails(
    module: ShopModuleMeta,
    state: ModuleState,
    onClose: () -> Unit,
    onInstall: () -> Unit,
    onActivate: () -> Unit,
    onDeactivate: () -> Unit,
    onUpdate: () -> Unit,
    readOnly: Boolean = false
) {
    val galleryItems = module.moduleGalleryAssetIds
        .filter { it > 0 }
        .distinct()
        .mapIndexed { index, assetId ->
            ShopDetailGalleryItem(
                title = if (index == 0) module.title else "${module.title} ${index + 1}",
                imagePath = moduleAssetUrl(assetId),
                description = module.description
            )
        }
    val safeGalleryItems = galleryItems.ifEmpty {
        listOf(
            ShopDetailGalleryItem(
                title = module.title,
                imagePath = "",
                description = module.description.ifBlank { "Keine Beschreibung hinterlegt." }
            )
        )
    }

    var currentIndex by remember(module.key) { mutableStateOf(0) }
    var expandedImagePath by remember(module.key) { mutableStateOf<String?>(null) }
    if (currentIndex > safeGalleryItems.lastIndex) currentIndex = 0

    val primaryItem = safeGalleryItems[currentIndex]
    val visibleItems = listOf(primaryItem)
    val canStep = safeGalleryItems.size > 1

    val actionLabel = when (state) {
        ModuleStateVal.AVAILABLE -> "Hinzufügen"
        ModuleStateVal.PURCHASED -> "Installieren"
        ModuleStateVal.DISABLED -> "Aktivieren"
        ModuleStateVal.INSTALLED -> "Deaktivieren"
        ModuleStateVal.UPDATE_AVAILABLE -> "Aktualisieren"
        ModuleStateVal.MAINTENANCE -> "Wartung"
        ModuleStateVal.BLOCKED -> "Gesperrt"
        else -> statusTextForDetails(state)
    }
    val actionEnabled = state != ModuleStateVal.MAINTENANCE &&
        state != ModuleStateVal.BLOCKED &&
        state != ModuleStateVal.INSTALLING &&
        state != ModuleStateVal.UNINSTALLING
    val categoryLabel = module.modulKategorie.trim()

    Div({
        style {
            width(100.percent)
            maxWidth(1312.px)
            property("margin", "0 auto")
            padding(8.px, 12.px, 36.px, 12.px)
            boxSizing("border-box")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(26.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.FlexStart)
                    gap(20.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(12.px)
                        flexGrow(1)
                        minWidth(0.px)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(14.px)
                            minWidth(0.px)
                        }
                    }) {
                        LumaticaButton(
                            label = "Zurück",
                            onClick = onClose,
                            variant = LumaticaButtonVariant.Standard,
                            heightPx = 36
                        )
                        H2({
                            style {
                                margin(0.px)
                                color(Color("#f5f6f8"))
                                fontSize(34.px)
                                fontWeight("700")
                                lineHeight("1.08")
                                paddingRight(24.px)
                            }
                        }) { Text(module.title) }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                            flexWrap(FlexWrap.Wrap)
                            color(Color("#aeb7c6"))
                            fontSize(14.px)
                            fontWeight("500")
                        }
                    }) {
                        Span { Text("Version ${module.version}") }
                        MetaDivider()
                        Span { Text(categoryLabel) }
                        MetaDivider()
                        Span { Text("${module.enabledScreens.size} Screens") }
                        if (state != ModuleStateVal.AVAILABLE) {
                            MetaDivider()
                            Span({ style { color(Color("#d7dde8")); fontWeight("600") } }) { Text(statusTextForDetails(state)) }
                        }
                    }
                }

                if (!readOnly) {
                    Div({ style { width(196.px); flexShrink(0) } }) {
                        LumaticaButton(
                            label = actionLabel,
                            onClick = {
                                when (state) {
                                    ModuleStateVal.AVAILABLE -> onInstall()
                                    ModuleStateVal.PURCHASED -> onInstall()
                                    ModuleStateVal.DISABLED -> onActivate()
                                    ModuleStateVal.INSTALLED -> onDeactivate()
                                    ModuleStateVal.UPDATE_AVAILABLE -> onUpdate()
                                }
                            },
                            variant = LumaticaButtonVariant.Action,
                            enabled = actionEnabled,
                            heightPx = 44
                        )
                    }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "minmax(0, 1fr)")
                        gap(18.px)
                        position(Position.Relative)
                    }
                }) {
                    visibleItems.forEachIndexed { slot, item ->
                        Div({
                            style {
                                height(360.px)
                                borderRadius(22.px)
                                overflow("hidden")
                                position(Position.Relative)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                backgroundColor(Color("rgba(255,255,255,0.03)"))
                                cursor(if (item.imagePath.isNotBlank()) "pointer" else "default")
                            }
                            onClick {
                                if (item.imagePath.isNotBlank()) expandedImagePath = item.imagePath
                            }
                        }) {
                            if (item.imagePath.isNotBlank()) {
                                Img(src = item.imagePath, attrs = {
                                    style {
                                        width(100.percent)
                                        height(100.percent)
                                        property("object-fit", "cover")
                                    }
                                })
                            } else {
                                Span({
                                    style {
                                        color(Color("#93a0b3"))
                                        fontSize(14.px)
                                        fontWeight("600")
                                    }
                                }) { Text("Kein Bild") }
                            }

                            if (safeGalleryItems.size > 1 || slot == 0) {
                                Span({
                                    style {
                                        position(Position.Absolute)
                                        left(16.px)
                                        bottom(16.px)
                                        padding(7.px, 12.px)
                                        borderRadius(999.px)
                                        backgroundColor(Color("rgba(8,10,14,0.58)"))
                                        color(Color("#f5f6f8"))
                                        fontSize(12.px)
                                        fontWeight("600")
                                        property("backdrop-filter", "blur(12px)")
                                    }
                                }) { Text(item.title) }
                            }
                        }
                    }

                    Img(src = "images/left.svg", attrs = {
                        style {
                            position(Position.Absolute)
                            left((-28).px)
                            top(50.percent)
                            property("transform", "translateY(-50%)")
                            width(18.px)
                            height(18.px)
                            cursor(if (canStep) "pointer" else "default")
                            opacity(if (canStep) 0.86 else 0.28)
                            property("filter", "brightness(0) invert(1)")
                        }
                        onClick {
                            if (canStep) currentIndex = if (currentIndex == 0) safeGalleryItems.lastIndex else currentIndex - 1
                        }
                    })

                    Img(src = "images/right.svg", attrs = {
                        style {
                            position(Position.Absolute)
                            right((-28).px)
                            top(50.percent)
                            property("transform", "translateY(-50%)")
                            width(18.px)
                            height(18.px)
                            cursor(if (canStep) "pointer" else "default")
                            opacity(if (canStep) 0.86 else 0.28)
                            property("filter", "brightness(0) invert(1)")
                        }
                        onClick {
                            if (canStep) currentIndex = (currentIndex + 1) % safeGalleryItems.size
                        }
                    })
                }

                Div({
                    style {
                        width(100.percent)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(10.px)
                            flexWrap(FlexWrap.Wrap)
                            justifyContent(JustifyContent.Center)
                        }
                    }) {
                        galleryItems.forEachIndexed { index, item ->
                            Div({
                                style {
                                    width(120.px)
                                    height(84.px)
                                    borderRadius(14.px)
                                    overflow("hidden")
                                    cursor("pointer")
                                    position(Position.Relative)
                                    property("border", if (index == currentIndex) "2px solid ${UISettings.ACCENT}" else "1px solid rgba(255,255,255,0.08)")
                                    backgroundColor(Color("rgba(255,255,255,0.03)"))
                                    boxSizing("border-box")
                                }
                                onClick { currentIndex = index }
                            }) {
                                if (item.imagePath.isNotBlank()) {
                                    Img(src = item.imagePath, attrs = {
                                        style {
                                            width(100.percent)
                                            height(100.percent)
                                            property("object-fit", "cover")
                                        }
                                    })
                                }
                            }
                        }
                    }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(14.px)
                    paddingTop(8.px)
                }
            }) {
                H3({
                    style {
                        margin(0.px)
                        color(Color("#f5f6f8"))
                        fontSize(28.px)
                        fontWeight("700")
                        lineHeight("1.15")
                    }
                }) { Text("Übersicht") }

                P({
                    style {
                        margin(0.px)
                        color(Color("#aeb7c6"))
                        fontSize(16.px)
                        lineHeight("1.85")
                        property("white-space", "pre-wrap")
                        maxWidth(980.px)
                    }
                }) {
                    Text(module.description)
                }
            }
        }
    }

    expandedImagePath?.let { imagePath ->
        Div({
            style {
                position(Position.Fixed)
                property("inset", "0")
                backgroundColor(Color.transparent)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("z-index", "150000")
                padding(32.px)
                boxSizing("border-box")
            }
            onClick { expandedImagePath = null }
        }) {
            Div({
                style {
                    width(1120.px)
                    maxWidth(92.vw)
                    height(640.px)
                    maxHeight(84.vh)
                    borderRadius(28.px)
                    overflow("hidden")
                    position(Position.Relative)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    backgroundColor(Color("rgba(255,255,255,0.03)"))
                    property("box-shadow", "0 28px 80px rgba(0,0,0,0.42)")
                    boxSizing("border-box")
                }
                onClick { it.stopPropagation() }
            }) {
                Img(src = imagePath, attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                        display(DisplayStyle.Block)
                    }
                })
            }
        }
    }
}

@Composable
private fun MetaDivider() {
    Span({ style { color(Color("rgba(126,138,158,0.80)")) } }) { Text("|") }
}

private data class ShopDetailGalleryItem(
    val title: String,
    val imagePath: String,
    val description: String
)

private fun statusTextForDetails(state: ModuleState): String = when (state) {
    ModuleStateVal.AVAILABLE -> "Verfuegbar"
    ModuleStateVal.PURCHASED -> "Gekauft"
    ModuleStateVal.INSTALLED -> "Installiert"
    ModuleStateVal.UPDATE_AVAILABLE -> "Update verfuegbar"
    ModuleStateVal.DISABLED -> "Deaktiviert"
    ModuleStateVal.INSTALLING -> "Installiere"
    ModuleStateVal.UNINSTALLING -> "Deinstalliere"
    ModuleStateVal.MAINTENANCE -> "Wartung"
    ModuleStateVal.BLOCKED -> "Gesperrt"
    else -> state
}
